/**
 * Centralized proof statistics registry.
 *
 * Single source of truth for all marketing statistics used across the site.
 * When new research drops or DB stats change, update this file and all
 * landing pages, guides, and components that import from here will reflect
 * the new numbers.
 *
 * Categories:
 *   - platform: Verified live platform stats
 *   - research: Original CodePulse research study data
 *   - external: Third-party research citations
 *   - testimonial: Customer quotes with metrics
 */

export interface ProofStat {
  /** Unique key for programmatic lookup */
  key: string;
  /** Display value (e.g., "500K+", "92%", "47%") */
  value: string;
  /** Human-readable label (e.g., "PRs Analyzed") */
  label: string;
  /** Full description for tooltips or expanded views */
  description?: string;
  /** Source of the statistic */
  source: string;
  /** When this stat was last verified/updated */
  sourceDate: string;
  /** URL to source (for citations in guides) */
  sourceUrl?: string;
  /** Category for filtering */
  category: 'platform' | 'research' | 'external' | 'testimonial';
}

// ---------------------------------------------------------------------------
// Platform Stats (verified against the production admin dashboard)
// ---------------------------------------------------------------------------

export const PLATFORM_STATS = {
  developersRepresented: {
    key: 'platform_developers_represented',
    value: '1,000+',
    label: 'Developers Represented',
    description: 'Developers represented across connected CodePulse organizations',
    source: 'CodePulse production admin dashboard',
    sourceDate: '2026-07-27',
    category: 'platform' as const,
  },
  setupTime: {
    key: 'platform_setup_time',
    value: '5 min',
    label: 'Setup Time',
    description: 'Average time from GitHub connect to first insights',
    source: 'CodePulse Platform',
    sourceDate: '2026-03-01',
    category: 'platform' as const,
  },
  historyDepth: {
    key: 'platform_history_depth',
    value: '6 months',
    label: 'History on Day One',
    description: 'Historical data imported on initial sync',
    source: 'CodePulse Platform',
    sourceDate: '2026-03-01',
    category: 'platform' as const,
  },
};

// ---------------------------------------------------------------------------
// Research Stats (from our original studies)
// ---------------------------------------------------------------------------

export const RESEARCH_STATS = {
  // 2025 Benchmark Study
  benchmark2025_prs: {
    key: 'research_2025_prs_analyzed',
    value: '803K+',
    label: 'PRs Analyzed',
    description: '802,979 merged pull requests analyzed from GitHub Archive / BigQuery',
    source: '2025 CodePulse Engineering Benchmarks',
    sourceDate: '2025-10-01',
    sourceUrl: '/research/2025-engineering-benchmarks',
    category: 'research' as const,
  },
  benchmark2025_selfMerge: {
    key: 'research_2025_self_merge',
    value: '71%',
    label: 'Self-merge rate',
    description: '71% of PRs merged without any peer review',
    source: '2025 CodePulse Engineering Benchmarks',
    sourceDate: '2025-10-01',
    sourceUrl: '/research/2025-engineering-benchmarks',
    category: 'research' as const,
  },
  benchmark2025_waitTime: {
    key: 'research_2025_wait_time',
    value: '92%',
    label: 'Cycle time spent waiting',
    description: '92% of cycle time is spent waiting for review, not coding',
    source: '2025 CodePulse Engineering Benchmarks',
    sourceDate: '2025-10-01',
    sourceUrl: '/research/2025-engineering-benchmarks',
    category: 'research' as const,
  },
  benchmark2025_medianCycleTime: {
    key: 'research_2025_median_cycle',
    value: '3h',
    label: 'Median cycle time (reviewed PRs)',
    description: 'Median cycle time for PRs that receive at least one review',
    source: '2025 CodePulse Engineering Benchmarks',
    sourceDate: '2025-10-01',
    sourceUrl: '/research/2025-engineering-benchmarks',
    category: 'research' as const,
  },
  benchmark2025_firstTimerPenalty: {
    key: 'research_2025_first_timer',
    value: '10.9x',
    label: 'First-timer wait multiplier',
    description: 'First-time contributors wait 10.9x longer for review',
    source: '2025 CodePulse Engineering Benchmarks',
    sourceDate: '2025-10-01',
    sourceUrl: '/research/2025-engineering-benchmarks',
    category: 'research' as const,
  },
  benchmark2025_wednesdayPeak: {
    key: 'research_2025_wednesday',
    value: '23.5%',
    label: 'Merges on Wednesday',
    description: 'Wednesday is the new peak merge day (23.5% of weekly merges)',
    source: '2025 CodePulse Engineering Benchmarks',
    sourceDate: '2025-10-01',
    sourceUrl: '/research/2025-engineering-benchmarks',
    category: 'research' as const,
  },
  benchmark2025_botDecline: {
    key: 'research_2025_bot_decline',
    value: '62% → 15%',
    label: 'Bot PR decline',
    description: 'Bot PRs declined from 62% to 15% between 2022-2025',
    source: '2025 CodePulse Engineering Benchmarks',
    sourceDate: '2025-10-01',
    sourceUrl: '/research/2025-engineering-benchmarks',
    category: 'research' as const,
  },

  // 2024 Code Review Study
  codeReview2024_prs: {
    key: 'research_2024_prs_analyzed',
    value: '3.4M',
    label: 'PRs analyzed in 2024 study',
    source: '2024 CodePulse Code Review Study',
    sourceDate: '2024-10-01',
    sourceUrl: '/research/code-review-study',
    category: 'research' as const,
  },
  codeReview2024_largePRs: {
    key: 'research_2024_large_pr_no_review',
    value: '83%',
    label: 'Large PRs with no review',
    description: '83% of large PRs (1000+ lines) ship without any review',
    source: '2024 CodePulse Code Review Study',
    sourceDate: '2024-10-01',
    sourceUrl: '/research/code-review-study',
    category: 'research' as const,
  },
};

// ---------------------------------------------------------------------------
// External Research Citations
// ---------------------------------------------------------------------------

export const EXTERNAL_STATS = {
  dora2024_eliteFrequency: {
    key: 'external_dora_elite',
    value: '973x',
    label: 'Elite deploy frequency multiplier',
    description: 'Elite teams deploy 973x more frequently than low performers',
    source: '2024 DORA Report',
    sourceDate: '2024-10-01',
    sourceUrl: 'https://dora.dev/research/',
    category: 'external' as const,
  },
  ieeeDefects: {
    key: 'external_ieee_defects',
    value: '80%',
    label: 'Defects from 20% of files',
    description: '20% of files account for 80% of defects in most codebases',
    source: 'IEEE Software Engineering Research',
    sourceDate: '2023-01-01',
    category: 'external' as const,
  },
};

// ---------------------------------------------------------------------------
// Testimonial Stats (customer quotes with measurable results)
// ---------------------------------------------------------------------------

export const TESTIMONIAL_STATS = {
  articleFiesta_idle: {
    key: 'testimonial_article_fiesta_idle',
    value: '31%',
    label: 'PRs idle — fixed in 3 weeks',
    description: 'Robert Striker discovered 31% of PRs were idle and fixed it in 3 weeks',
    source: 'Robert Striker, VP Engineering @ Article Fiesta',
    sourceDate: '2025-06-01',
    category: 'testimonial' as const,
  },
  ultraComm_reviewTime: {
    key: 'testimonial_ultra_review',
    value: '3 days → same-day',
    label: 'Review time reduction',
    description: 'Review time reduced from ~3 days to same-day',
    source: 'Jonathon Turner, Engineering Manager @ Ultra Communications',
    sourceDate: '2025-06-01',
    category: 'testimonial' as const,
  },
};

// ---------------------------------------------------------------------------
// Combined registry for programmatic access
// ---------------------------------------------------------------------------

export const ALL_PROOF_STATS: Record<string, ProofStat> = {
  ...Object.fromEntries(
    Object.values(PLATFORM_STATS).map((s) => [s.key, s])
  ),
  ...Object.fromEntries(
    Object.values(RESEARCH_STATS).map((s) => [s.key, s])
  ),
  ...Object.fromEntries(
    Object.values(EXTERNAL_STATS).map((s) => [s.key, s])
  ),
  ...Object.fromEntries(
    Object.values(TESTIMONIAL_STATS).map((s) => [s.key, s])
  ),
};

/**
 * Get a stat by its key.
 */
export function getProofStat(key: string): ProofStat | undefined {
  return ALL_PROOF_STATS[key];
}

/**
 * Get all stats in a category.
 */
export function getStatsByCategory(category: ProofStat['category']): ProofStat[] {
  return Object.values(ALL_PROOF_STATS).filter((s) => s.category === category);
}
